import { computed, defineComponent, h } from 'vue'
import { QDialog, QIcon } from 'quasar'

import { BsBtn } from '../index'
import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'
import { emitModel } from '../emitModel'

/**
 * The house dialog: a header with an optional icon and close cross, slotted content, and a footer whose
 * cancel button only appears when a `cancelText` is given.
 *
 * Every way out of it — the cross, either button, or QDialog's own dismiss — emits `input: false` as well
 * as its own event, because the caller drives visibility through v-model.
 */
export default defineComponent({
  name: 'BsModal',

  // See BsInput: undeclared DOM-named events are bound twice, once natively. `cancel`, `close` and `ok`
  // are this component's own.
  emits: [ 'input', 'update:modelValue', 'cancel', 'close', 'ok' ],

  props: {
    ...bsComponentProps,
    value: { type: Boolean, default: false },
    title: { type: String, default: void 0 },
    icon: { type: String, default: void 0 },
    okText: { type: String, default: void 0 },
    cancelText: { type: String, default: void 0 },
    hideClose: { type: Boolean, default: false },
    okButtonAsWarning: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: qDialog, set: setQDialogRef } = useTemplateRef<any>()

    const vm = useVm()

    return {
      qDialog,
      setQDialogRef,
      ...useBsComponent({ vm, props, emit }),

      /** A computed, as the class getter was — `$refs` is not reactive, so it resolves on first read. */
      dialog: computed(() => qDialog.value as QDialog)
    }
  },

  methods: {
    // Every `__renderX` is renamed: Vue does not proxy keys beginning with `_` onto the instance.
    renderCloseButton () {
      if (this.hideClose) return void 0

      return h(QIcon, {
        class: 'bs-modal__close',
        onClick: () => {
            emitModel(this, false)
            this.$emit('close')
          },
        name: 'app:close-circle'
      })
    },

    renderTitle () {
      return h('div', {
        class: 'bs-modal__title'
      }, [
        this.icon && h(QIcon, {
          class: 'on-left',
          name: this.icon
        }),
        this.title && h('h2', this.title)
      ])
    },

    renderHeader () {
      return h('div', {
        class: 'bs-modal__header'
      }, [
        this.renderTitle(),
        this.renderCloseButton()
      ].filter(child => child !== void 0))
    },

    renderOkButton () {
      return h(BsBtn, {
        large: true,
        warning: this.okButtonAsWarning,
        onClick: () => {
            emitModel(this, false)
            this.$emit('close', false)
            this.$emit('ok', false)
          },
        'data-model': 'btnModalOk'
      // BsBtn is a component: a bare string child is not rendered, it has to be a slot.
      }, { default: () => this.okText || 'DONE' })
    },

    renderCancelButton () {
      return h(BsBtn, {
        large: true,
        secondary: true,
        warning: !this.okButtonAsWarning,
        onClick: () => {
            emitModel(this, false)
            this.$emit('close', false)
            this.$emit('cancel', false)
          },
        'data-model': 'btnModalCancel'
      }, { default: () => this.cancelText })
    },

    renderFooter () {
      return h('div', {
        class: 'bs-modal__footer'
      /**
       * Filtered: Vue 2 dropped an undefined entry from a children array, Vue 3 keeps it as an empty
       * placeholder. Nothing renders either way, but it changes the shape of the tree.
       */
      }, [
        this.cancelText === void 0 ? void 0 : this.renderCancelButton(),
        this.renderOkButton()
      ].filter(child => child !== void 0))
    },

    renderContent () {
      return h('div', {
        class: 'bg-white'
      }, [
        this.renderHeader(),
        this.slot(this, 'default'),
        this.renderFooter()
      ].filter(child => child !== void 0))
    },

    renderDialog () {
      const listeners = {
        input: (val: boolean) => {
          // this.dialog.hide()
          emitModel(this, val)
          this.$emit('close')
        }
      }

      return h(QDialog, {
        ref: this.setQDialogRef,
        // Quasar 2's v-model prop and event; the bundle's own API stays `value` / `input`.
        modelValue: this.value,
        persistent: true,
        // `class`, not `contentClass`: Quasar 2 dropped that prop from QDialog. Both versions put the
        // value on the same element — the root `.q-dialog` — so this is the same DOM under a new name.
        class: 'bs-modal',
        maximized: this.$q.screen.xs,
        ...quasarHandlers(this.getListeners(listeners))
      // QDialog is a component: children have to be a slot function.
      }, { default: () => [
        this.renderContent()
      ] })
    }
  },

  render () {
    return this.renderDialog()
  }
})
